import { useParams } from "react-router-dom";
import type { DocumentDto } from "../api/types";
import { getPost } from "../api/content";
import { useAsync } from "../hooks/useAsync";
import { Loading, ErrorState } from "../components/Spinner";
import { DocumentView } from "../components/document/DocumentView";

/**
 * A single journal entry, addressed by slug (/blog/:slug) — the friendly URL
 * required for the blog. It loads the post, adapts it to the uniform document
 * shape, and renders the shared DocumentView so the entry shows its Markdown
 * body and the same comment thread as /documents/BLOG-POST-<id>.
 */
export function BlogPostPage() {
  const { slug = "" } = useParams();
  const { data: post, loading, error } = useAsync(() => getPost(slug), [slug]);

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <Loading label="Retrieving entry…" />
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="page">
        <div className="container">
          <ErrorState label={error ?? "That journal entry could not be found."} />
        </div>
      </div>
    );
  }

  const doc: DocumentDto = {
    contentId: `BLOG-POST-${post.id}`,
    contentType: "BLOG_POST",
    sourceId: post.id,
    title: post.title,
    summary: post.excerpt,
    bodyMarkdown: post.contentMarkdown,
    url: `/blog/${post.slug}`,
    collection: post.collection,
    thumbnailUrl: post.coverImageUrl,
    provider: null,
    source: null,
    occurredAt: post.publishedAt,
    tags: post.tags,
    visibility: "PUBLIC",
    sharedWith: [],
  };

  return <DocumentView doc={doc} backTo="/blog" backLabel="← Back to the Journal" />;
}
